import { Collapse, Stack, Title, useMantineTheme } from '@mantine/core'
import { useDisclosure, useMediaQuery } from '@mantine/hooks'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import RootButton from '@/components/Buttons/RootButton'
import { useElectionsContext } from '@/contexts/ElectionsContext'
import { CandidateStatus, NominationData } from '@/hooks/useGovernance'
import { useAuth } from '@/plugins/auth'
import { emptyBalance, ProcessedBalance } from '@/utils/utils'

import { Amount } from '../../CurrencyInput'
import NoData from '../../NoData'
import ElectionsWinner from './ElectionsWinner'
import ListNumber from './ListNumber'
import NomineeCard from './NomineeCard'
import VotesCard from './VotesCard'

export type NominationActionProps = {
  nominee: string
  status: CandidateStatus
  isMobile: boolean | undefined
  isBreakpoint: boolean | undefined
}

export type CampaignActionProps = {
  campaignLinks: Map<string, string>
  loadingCampaignLinks: boolean
}

export type VotingActionProps = {
  isBreakpoint: boolean | undefined
  votes: { [key: string]: Amount }
  nominee: string
  status: CandidateStatus
  handleVoteChange: (nominee: string, value: Amount) => void
  remainingVoteBalance: ProcessedBalance
  votingActive: boolean
}

export interface ResultsActionProps {
  status: CandidateStatus
}

export type ActionProps =
  | NominationActionProps
  | CampaignActionProps
  | VotingActionProps
  | ResultsActionProps

interface NomineesListProps<T extends ActionProps = ActionProps> {
  title?: string
  candidates: NominationData[]
  phase?: 'nomination' | 'campaign' | 'voting' | 'results'
  button?: React.ReactNode
  action?: React.FC<T>
  actionProps?: T
  subHeader?: React.ReactNode
  extraInfo?: React.ReactNode
}

const NomineesList = <T extends ActionProps = ActionProps>({
  title = '',
  candidates,
  phase = 'nomination',
  button = null,
  action,
  actionProps,
  subHeader = null,
  extraInfo = null,
}: NomineesListProps<T>) => {
  const theme = useMantineTheme()
  const { t } = useTranslation()
  const isBreakpoint = useMediaQuery(`(max-width: 920px)`)
  const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.sm})`)
  const { address } = useAuth()

  const nomination = useMemo(() => phase === 'nomination', [phase])
  const campaign = useMemo(() => phase === 'campaign', [phase])
  const voting = useMemo(() => phase === 'voting', [phase])
  const results = useMemo(() => phase === 'results', [phase])

  const { userVoteTotals, candidateVoteTotals, isLoading, error, refetchData } =
    useElectionsContext()
  const [openedViewMore, { toggle }] = useDisclosure(false)

  // Filter accepted candidates
  const acceptedCandidates = useMemo(
    () =>
      candidates.filter(
        cand =>
          cand.status === 'ACCEPTED' &&
          cand.nominee.toLowerCase() !== address?.toLowerCase() &&
          cand.cid !== ''
      ),
    [candidates, address]
  )

  // Randomize accepted candidates
  const randomizedAcceptedCandidates = useMemo(() => {
    const randCands = [...acceptedCandidates]
    for (let i = randCands.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1))
      ;[randCands[i], randCands[j]] = [randCands[j], randCands[i]]
    }
    return randCands
  }, [acceptedCandidates.length])

  // Sort candidates by status
  const sortedCandidates = useMemo(() => {
    if (nomination || campaign) {
      const userCandidate = candidates.find(
        cand => cand.nominee.toLowerCase() === address?.toLowerCase()
      )

      // Separate candidates by status
      const acceptedCandidatesNoApp = candidates.filter(
        cand =>
          cand.status === 'ACCEPTED' &&
          cand.nominee.toLowerCase() !== address?.toLowerCase() &&
          cand.cid === ''
      )

      const pendingCandidates = candidates.filter(
        cand => cand.status === 'PENDING' && cand.nominee.toLowerCase() !== address?.toLowerCase()
      )

      const rejectedCandidates = candidates.filter(
        cand => cand.status === 'REJECTED' && cand.nominee.toLowerCase() !== address?.toLowerCase()
      )

      const removedCandidates = candidates.filter(
        cand => cand.status === 'REMOVED' && cand.nominee.toLowerCase() !== address?.toLowerCase()
      )

      // Combine all candidates in the desired order
      return [
        ...(userCandidate ? [userCandidate] : []),
        ...randomizedAcceptedCandidates,
        ...acceptedCandidatesNoApp,
        ...pendingCandidates,
        ...rejectedCandidates,
        ...removedCandidates,
      ]
    }
    if (voting) {
      // sort accepted candidates by votes
      const sortedCandidatesByVotes = candidates
        .filter(
          cand =>
            cand.status === 'ACCEPTED' && cand.nominee.toLowerCase() !== address?.toLowerCase()
        )
        .map(cand => ({
          ...cand,
          votes: candidateVoteTotals[cand.nominee] || emptyBalance,
        }))
        .sort((a, b) => Number(b.votes.value) - Number(a.votes.value))

      const userCandidate = candidates.find(
        cand => cand.nominee.toLowerCase() === address?.toLowerCase()
      )

      const removedCandidates = candidates.filter(
        cand => cand.status === 'REMOVED' && cand.nominee.toLowerCase() !== address?.toLowerCase()
      )

      // Combine
      return [
        ...(userCandidate ? [userCandidate] : []),
        ...sortedCandidatesByVotes,
        ...removedCandidates,
      ]
    }
    return candidates
  }, [
    candidates,
    address,
    nomination,
    campaign,
    voting,
    randomizedAcceptedCandidates,
    candidateVoteTotals,
  ])

  return (
    <Stack gap="15px">
      <Stack
        style={{
          flexDirection: 'row',
          justifyContent: 'space-between',
          position: 'sticky',
          top: '100px',
          padding: '10px 0px',
          alignItems: 'flex-end',
          backgroundColor: theme.colors.background[0],
          zIndex: !button ? 1 : 200,
        }}
      >
        <Stack>
          {title && <Title order={4}>{title}</Title>}
          {subHeader}
        </Stack>
        {button}
      </Stack>
      {sortedCandidates.length > 0 ? (
        <Stack gap="15px" style={{ marginTop: '2px' }}>
          {results && (
            <ElectionsWinner
              nominee={sortedCandidates[0].nominee}
              nominator={sortedCandidates[0].nominator}
              userVoteTotals={userVoteTotals}
              candidateVoteTotals={candidateVoteTotals}
              isLoading={isLoading}
            />
          )}
          {sortedCandidates.map(({ nominee, nominator, status, cid }, index) => {
            const showCollapse = index > 2 && results
            const List = !(results && index === 0) && (
              <Stack
                gap={isMobile ? '10px' : ''}
                style={{
                  flexDirection: isBreakpoint && (results || voting) ? 'column' : 'row',
                }}
              >
                {isMobile && results && (
                  <ListNumber index={index} phase={phase} isMobile={isMobile} fullWidth />
                )}
                <Stack gap={isMobile ? '10px' : ''} style={{ flexDirection: 'row', width: '100%' }}>
                  {!isMobile && <ListNumber index={index} phase={phase} isMobile={isMobile} />}
                  <NomineeCard<T>
                    {...{
                      nominee,
                      nominator,
                      status,
                      cid,
                      phase,
                      index,
                      isBreakpoint,
                      isMobile,
                      isResults: results,
                    }}
                    action={action}
                    actionProps={
                      {
                        ...actionProps,
                        nominee,
                        status,
                        isMobile,
                        isBreakpoint,
                      } as T
                    }
                  />
                </Stack>
                {(results || voting) && (
                  <>
                    {address && (
                      <VotesCard
                        index={index}
                        phase={phase}
                        nominee={nominee}
                        isBreakpoint={isBreakpoint}
                        type="yours"
                        votes={userVoteTotals[nominee]}
                        loading={isLoading}
                        removed={status === 'REMOVED'}
                        isError={error.isError}
                        refetchData={refetchData}
                      />
                    )}
                    <VotesCard
                      index={index}
                      nominee={nominee}
                      phase={phase}
                      isBreakpoint={isBreakpoint}
                      type="all"
                      votes={candidateVoteTotals[nominee]}
                      loading={isLoading}
                      removed={status === 'REMOVED'}
                      isError={error.isError}
                      refetchData={refetchData}
                    />
                  </>
                )}
              </Stack>
            )
            return (
              <React.Fragment key={nominee}>
                {showCollapse ? <Collapse in={openedViewMore}>{List}</Collapse> : List}
              </React.Fragment>
            )
          })}
        </Stack>
      ) : (
        <NoData missing="nominees" />
      )}

      {results && (
        <RootButton secondary style1={false} style={{ alignSelf: 'center' }} onClick={toggle}>
          {openedViewMore ? t('buttons.viewLess', 'View Less') : t('buttons.viewMore', 'View More')}
        </RootButton>
      )}
      {extraInfo}
    </Stack>
  )
}

export default NomineesList
